import { useMemo, useState, useEffect, useRef } from "react";
import ReactGA from "react-ga4";
import { useData } from "./contexts/DataContext";
import { useAuth } from "./contexts/AuthContext";
import { createImageProps } from "./utils/imageUtils";
import { useNavigate } from "react-router-dom";
import PriceHistoryGraph from "./components/PriceHistoryGraph";
import { usePostHogTracking } from "./hooks/usePostHogTracking";
import { sizeNameMap } from "./utils/sizeNameUtils";
import Login from "./Login";
import CareGuideContent from "./components/CareGuideContent";
import BestPriceSummary, {
  getDealTextColor,
} from "./components/BestPriceSummary";
import SizeFilter from "./components/SizeFilter";
import {
  deduplicateListings,
  formatSizeRange,
  getBestPricesPerSize,
  listingMatchesSizeFilter,
} from "./utils/listingUtils";
import {
  ListingGroupFrame,
  ListingDetailsTable,
} from "./components/listings/ListingGroupCard";
import ListingHeaderActions from "./components/listings/ListingHeaderActions";
import ListingImage from "./components/listings/ListingImage";
import SpeciesListingHero from "./components/listings/SpeciesListingHero";
import { bestHistoricalSavingsPercent } from "./components/listings/HistoricalSavingsBadge";
import StoreName from "./components/StoreName";

const FishListing = ({
  group,
  groupVisibility,
  setGroupVisibility,
  storesData,
  getFishProductUrl,
}) => {
  const isExpanded = Boolean(groupVisibility[group.scientific_name]);
  const [activeTab, setActiveTab] = useState(isExpanded ? "sizes" : null);
  const listingCardRef = useRef(null);
  const scrollAfterExpansionRef = useRef(false);

  // Add state for reviews
  const [speciesReviews, setSpeciesReviews] = useState([]);
  const [loadingSpeciesReviews, setLoadingSpeciesReviews] = useState(true);
  const [expandedReviews, setExpandedReviews] = useState({});

  // Care guide state
  const [careGuide, setCareGuide] = useState(null);
  const [careGuideLoading, setCareGuideLoading] = useState(false);
  const careGuideScrollRef = useRef(null);

  // Add state for login modal
  const [showLoginModal, setShowLoginModal] = useState(false);

  // Add state for listing filters and sort in All Listings tab
  const [listingSortKey, setListingSortKey] = useState("bestSavingsPercentage");
  const [listingSortDir, setListingSortDir] = useState("desc");
  const [listingSizeFilter, setListingSizeFilter] = useState([0, 36]);
  const [includeNumericSizesLocal, setIncludeNumericSizesLocal] =
    useState(true);
  const [includeNonNumericSizesLocal, setIncludeNonNumericSizesLocal] =
    useState(true);
  const [selectedNonNumericSizes, setSelectedNonNumericSizes] = useState(
    Object.values(sizeNameMap)
  );

  // Get context data including reviews and user data
  const {
    apiEndpoints,
    cachedImageFailed,
    markCachedImageFailed,
    markImageLoaded,
    imageLoaded,
    reviewsData,
    dataLoaded,
    userFavorites,
    userSubscriptions,
    isFavorited,
    getSpeciesSubscriptions,
    hasActiveSubscriptions: hasActiveSubscriptionsHelper,
    addFavorite,
    removeFavorite,
    loading,
    BACKEND_URL,
    fetchCareGuideByScientificName,
    careGuideDetails,
  } = useData();

  // Get auth context
  const { isAuthenticated, token } = useAuth();

  // Add navigation hook
  const navigate = useNavigate();

  // Add PostHog tracking
  const { trackFishInteraction } = usePostHogTracking();

  const speciesSubscriptions = useMemo(() => {
    return getSpeciesSubscriptions(userSubscriptions, group.scientific_name);
  }, [userSubscriptions, getSpeciesSubscriptions, group.scientific_name]);

  const hasActiveSubscriptions = useMemo(() => {
    return hasActiveSubscriptionsHelper(
      speciesSubscriptions,
      group.scientific_name
    );
  }, [speciesSubscriptions, group.scientific_name]);

  const updateGroupVisibility = (newVisibility) => {
    ReactGA.event({
      category: "Interaction",
      action: newVisibility ? "Expand Fish" : "Collapse Fish",
      label: group.scientific_name,
    });

    // Track with PostHog
    trackFishInteraction(newVisibility ? "expand_fish" : "collapse_fish", {
      scientific_name: group.scientific_name,
      common_name: group.common_name,
      total_listings: group.total_listings,
    });

    setGroupVisibility((prev) => ({
      ...prev,
      [group.scientific_name]: newVisibility,
    }));
  };

  const handleGroupVisibilityToggle = () => {
    if (isExpanded) {
      setActiveTab(null);
      updateGroupVisibility(false);
      return;
    }

    scrollAfterExpansionRef.current = true;
    setActiveTab("sizes");
    updateGroupVisibility(true);
  };

  useEffect(() => {
    if (isExpanded) {
      setActiveTab((currentTab) => currentTab || "sizes");
      if (scrollAfterExpansionRef.current) {
        scrollAfterExpansionRef.current = false;
        window.requestAnimationFrame(() => {
          listingCardRef.current?.scrollIntoView?.({
            behavior: "smooth",
            block: "start",
          });
        });
      }
    } else {
      setActiveTab(null);
    }
  }, [isExpanded]);

  const availableSizes = useMemo(() => {
    var sizes = Object.keys(group.sizes);
    var min_size = sizes
      .map((size) => parseInt(size))
      .filter((size) => !isNaN(size) && size >= 0)
      .reduce((a, b) => Math.min(a, b), Infinity);
    var max_size = sizes
      .map((size) => parseInt(size))
      .filter((size) => !isNaN(size) && size >= 0)
      .reduce((a, b) => Math.max(a, b), -Infinity);
    var sizeString = null;
    if (min_size == Infinity) {
      if (max_size != -Infinity) {
        sizeString = `${max_size}"`;
      }
    } else if (max_size == -Infinity) {
      sizeString = `${min_size}"`;
    } else if (min_size == max_size) {
      sizeString = `${min_size}"`;
    } else {
      sizeString = `${min_size}-${max_size}"`;
    }
    const result = [];
    if (sizeString != null) {
      result.push(sizeString);
    }

    var other_sizes = [
      ...new Set(
        sizes
          .filter((size) => isNaN(parseInt(size)) || size < 0)
          .map((size) => (size == -1 ? "Unknown" : sizeNameMap[size] || size))
      ),
    ].sort((a, b) => {
      const sizeOrder = Object.values(sizeNameMap);
      return sizeOrder.indexOf(a) - sizeOrder.indexOf(b);
    });
    result.push(...other_sizes);
    return result;
  }, [group.sizes]);

  function sortSizes(a, b) {
    var aNum = parseInt(a);
    var bNum = parseInt(b);
    if (aNum == -1) {
      aNum = NaN;
    }
    if (bNum == -1) {
      bNum = NaN;
    }

    if (isNaN(aNum) && isNaN(bNum)) {
      const sizeOrder = Object.values(sizeNameMap);
      return sizeOrder.indexOf(a) - sizeOrder.indexOf(b);
    }
    if (isNaN(aNum)) return 1;
    if (isNaN(bNum)) return -1;

    return aNum - bNum;
  }

  // Deduplicated listings for listing-first view
  const deduplicatedListings = useMemo(() => {
    return deduplicateListings(group.sizes, group.sizes_meta);
  }, [group.sizes, group.sizes_meta]);

  const priceRange = useMemo(() => {
    const prices = deduplicatedListings
      .map((listing) => Number(listing.pricePerFish))
      .filter(Number.isFinite);

    if (prices.length === 0) return "Price unavailable";

    const minimumPrice = Math.min(...prices);
    const maximumPrice = Math.max(...prices);
    if (minimumPrice === maximumPrice) return `$${minimumPrice.toFixed(2)}`;

    return `$${minimumPrice.toFixed(2)} – $${maximumPrice.toFixed(2)}`;
  }, [deduplicatedListings]);

  // Best prices per size for summary chart
  const bestPricesPerSize = useMemo(() => {
    return getBestPricesPerSize(group.sizes, group.sizes_meta);
  }, [group.sizes, group.sizes_meta]);

  // Calculate numeric size range for local filter
  const numericSizeRange = useMemo(() => {
    const numericSizes = Object.keys(group.sizes)
      .map((s) => parseFloat(s))
      .filter((s) => !isNaN(s) && s >= 0);

    if (numericSizes.length === 0) {
      return null;
    }
    return [Math.min(...numericSizes), Math.max(...numericSizes)];
  }, [group.sizes]);

  // Available non-numeric sizes for filter
  const availableNonNumericSizes = useMemo(() => {
    return Object.keys(group.sizes)
      .filter((size) => isNaN(parseFloat(size)) || parseFloat(size) < 0)
      .map((size) => (size === "-1" ? "Unknown" : sizeNameMap[size] || size));
  }, [group.sizes]);

  useEffect(() => {
    setSelectedNonNumericSizes(availableNonNumericSizes);
  }, [availableNonNumericSizes]);

  // Initialize size filter when component mounts or sizes change
  useEffect(() => {
    setListingSizeFilter(numericSizeRange);
  }, [numericSizeRange]);

  // Filtered listings for All Listings tab. Sorting is handled by ListingDetailsTable.
  const filteredSortedListings = useMemo(() => {
    let filtered = [...deduplicatedListings];

    // Apply size filter
    filtered = filtered.filter((listing) =>
      listingMatchesSizeFilter(
        listing,
        listingSizeFilter,
        selectedNonNumericSizes,
        includeNumericSizesLocal,
        includeNonNumericSizesLocal
      )
    );

    return filtered;
  }, [
    deduplicatedListings,
    listingSizeFilter,
    includeNumericSizesLocal,
    includeNonNumericSizesLocal,
    selectedNonNumericSizes,
  ]);

  // Function to render star rating
  const renderStarRating = (rating, reviewCount) => {
    const stars = [];

    if (reviewCount == 0) {
      for (let i = 0; i < 5; i++) {
        stars.push(
          <span key={i} className="text-gray-300">
            ★
          </span>
        );
      }
    } else {
      const fullStars = Math.floor(rating);
      const hasHalfStar = rating % 1 >= 0.5;

      for (let i = 0; i < fullStars; i++) {
        stars.push(
          <span key={i} className="text-yellow-400">
            ★
          </span>
        );
      }

      if (hasHalfStar) {
        stars.push(
          <span key="half" className="text-yellow-400">
            ☆
          </span>
        );
      }

      const emptyStars = 5 - Math.ceil(rating);
      for (let i = 0; i < emptyStars; i++) {
        stars.push(
          <span key={`empty-${i}`} className="text-gray-300">
            ☆
          </span>
        );
      }
    }

    return stars;
  };

  // Function to filter reviews for this species from the global reviews data
  const filterReviewsForSpecies = (allReviews, scientificName) => {
    return allReviews
      .filter(
        (review) =>
          review.fish_reviews &&
          review.fish_reviews.some(
            (fishReview) =>
              fishReview.species_listing?.species?.scientific_name?.toLowerCase() ===
              scientificName.toLowerCase()
          )
      )
      .map((review) => ({
        ...review,
        this_fish_reviews: review.fish_reviews.filter(
          (fishReview) =>
            fishReview.species_listing?.species?.scientific_name?.toLowerCase() ===
            scientificName.toLowerCase()
        ),
      }));
  };

  // Update species reviews when global reviews data changes
  useEffect(() => {
    if (dataLoaded.reviews) {
      const filteredReviews = filterReviewsForSpecies(
        reviewsData,
        group.scientific_name
      );
      setSpeciesReviews(filteredReviews);
      setLoadingSpeciesReviews(false);
    }
  }, [reviewsData, group.scientific_name]);

  // Handle bell icon click
  const handleBellClick = (e) => {
    e.stopPropagation();

    const newTab =
      isExpanded && activeTab === "notifications" ? "sizes" : "notifications";
    setActiveTab(newTab);
    if (!isExpanded) updateGroupVisibility(true);

    ReactGA.event({
      category: "Notifications",
      action:
        newTab === "notifications"
          ? "Show Notifications"
          : "Hide Notifications",
      label: group.scientific_name,
    });
  };

  // Handle price history click
  const handlePriceHistoryClick = (e) => {
    e.stopPropagation();

    const newTab =
      isExpanded && activeTab === "priceHistory" ? "sizes" : "priceHistory";
    setActiveTab(newTab);
    if (!isExpanded) updateGroupVisibility(true);

    ReactGA.event({
      category: "Price History",
      action:
        newTab === "priceHistory" ? "Show Price History" : "Hide Price History",
      label: group.scientific_name,
    });

    // Track with PostHog
    trackFishInteraction(
      newTab === "priceHistory" ? "show_price_history" : "hide_price_history",
      {
        scientific_name: group.scientific_name,
        common_name: group.common_name,
      }
    );
  };

  const scientificName = group.scientific_name;
  const currentlyFavorited = useMemo(
    () => isFavorited(userFavorites, scientificName),
    [userFavorites, scientificName]
  );

  // Handle star icon click for favorites
  const handleStarClick = async (e) => {
    e.stopPropagation();

    if (showLoginModal) {
      setShowLoginModal(false);
      return;
    }

    if (!isAuthenticated) {
      // Show login modal when not authenticated
      setShowLoginModal(true);
      return;
    }

    if (loading.favorites) return;

    const scientificName = group.scientific_name;
    let success = false;
    if (isFavorited(userFavorites, scientificName)) {
      success = await removeFavorite(scientificName, token);
      if (success) {
        ReactGA.event({
          category: "Favorites",
          action: "Remove Favorite",
          label: scientificName,
        });
      }
    } else {
      success = await addFavorite(scientificName, token);
      if (success) {
        ReactGA.event({
          category: "Favorites",
          action: "Add Favorite",
          label: scientificName,
        });
      }
    }

    if (!success) {
      console.error("Failed to update favorite status");
    }
  };

  // Function to navigate to notifications page
  const handleManageNotifications = (expandCreateForm) => {
    ReactGA.event({
      category: "Notifications",
      action: "Navigate to Notifications Page",
      label: group.scientific_name,
    });

    // Navigate to notifications page with this species pre-selected and expand form
    navigate("/notifications", {
      state: {
        preselected_species: {
          scientific_name: group.scientific_name,
          common_name: group.common_name,
        },
        expandCreateForm: expandCreateForm,
      },
    });
  };

  // Function to format size display
  const formatSizeDisplay = (size) => {
    if (size.includes(" inches")) {
      return size; // Already formatted as "x-y inches"
    }
    return sizeNameMap[size] || size + (isNaN(parseInt(size)) ? "" : " inches");
  };

  // Handle rating click
  const handleRatingClick = (e) => {
    e.stopPropagation();
    const newTab =
      isExpanded && activeTab === "reviews" ? "sizes" : "reviews";
    setActiveTab(newTab);
    if (!isExpanded) {
      scrollAfterExpansionRef.current = true;
      updateGroupVisibility(true);
    }
  };

  // Toggle individual review expansion
  const toggleReviewExpansion = (reviewId) => {
    setExpandedReviews((prev) => ({
      ...prev,
      [reviewId]: !prev[reviewId],
    }));
  };

  // Function to navigate to write review page
  const handleWriteReview = () => {
    ReactGA.event({
      category: "Reviews",
      action: "Click Write Review",
      label: group.scientific_name,
    });

    // Navigate to reviews page with pre-filled species data
    navigate("/reviews", {
      state: {
        create_review: {
          scientific_name: group.scientific_name,
        },
      },
    });
  };

  const listingTitle = group.common_name.toLowerCase().endsWith(" sp.")
    ? `Assorted ${group.common_name.replace(/ sp\.$/i, "")} species`
    : group.common_name;

  const handleCareGuideTabClick = () => {
    const newTab =
      isExpanded && activeTab === "careGuide" ? "sizes" : "careGuide";
    setActiveTab(newTab);
    if (!isExpanded) {
      scrollAfterExpansionRef.current = true;
      updateGroupVisibility(true);
    }
    if (newTab === "careGuide" && !careGuide && !careGuideLoading) {
      setCareGuideLoading(true);
      fetchCareGuideByScientificName(group.scientific_name)
        .then((data) => setCareGuide(data))
        .catch(() => setCareGuide(null))
        .finally(() => setCareGuideLoading(false));
    }
    ReactGA.event({
      category: "Care Guide",
      action: newTab === "careGuide" ? "Show Care Guide" : "Hide Care Guide",
      label: group.scientific_name,
    });
    trackFishInteraction(
      newTab === "careGuide" ? "show_care_guide" : "hide_care_guide",
      { scientific_name: group.scientific_name, common_name: group.common_name }
    );
  };

  const handleSizesTabClick = () => {
    setActiveTab("sizes");
    if (!isExpanded) {
      scrollAfterExpansionRef.current = true;
      updateGroupVisibility(true);
    }
  };

  const headerActions = (
    <ListingHeaderActions
      averageRating={group.avg_rating}
      reviewCount={group.review_count}
      isReviewLoading={loadingSpeciesReviews}
      onReviewClick={handleRatingClick}
      favoriteActive={currentlyFavorited}
      onFavoriteClick={handleStarClick}
      favoriteTitle={
        !isAuthenticated
          ? "Login to favorite this species"
          : currentlyFavorited
          ? "Remove from favorites"
          : "Add to favorites"
      }
      loginTooltipOpen={showLoginModal}
      onLoginTooltipClose={() => setShowLoginModal(false)}
      loginTooltipMessage="Please log in to favorite this species."
    />
  );

  const headerImage = (
    <ListingImage
      imageUrl={group.image_url}
      alt={group.common_name || group.scientific_name}
      attributionName={group.scientific_name}
      attributionType="species"
      imageFrameClassName="species-listing-hero-image-fill"
      imageClassName="species-listing-hero-image-element"
      attributionClassName="species-listing-hero-attribution"
      attributionPosition=""
      isLoaded={Boolean(imageLoaded[group.scientific_name])}
      imageProps={createImageProps(
        group.scientific_name,
        apiEndpoints,
        cachedImageFailed,
        markCachedImageFailed,
        markImageLoaded
      )}
    />
  );

  const listingTabs = [
    {
      key: "careGuide",
      label: "Care Guide",
      icon: <span className="text-lg">{"\uD83D\uDCD6"}</span>,
      isActive: activeTab === "careGuide",
      onClick: handleCareGuideTabClick,
    },
    {
      key: "priceHistory",
      label: "Price History",
      icon: <img src="/pricehistory-optimized.webp" alt="Price History" className="w-5" />,
      isActive: activeTab === "priceHistory",
      onClick: handlePriceHistoryClick,
    },
    {
      key: "reviews",
      label: "Reviews",
      icon: <img src="/reviews-optimized.webp" alt="Reviews" className="w-5" />,
      isActive: activeTab === "reviews",
      onClick: handleRatingClick,
    },
    {
      key: "notifications",
      label: "Notifications",
      icon: (
        <img
          src="/bell-optimized.webp"
          alt="Notifications"
          className={`w-5 ${
            hasActiveSubscriptions ? "text-orange-500" : "text-gray-400"
          }`}
          style={{
            filter: hasActiveSubscriptions ? "none" : "grayscale(100%)",
          }}
        />
      ),
      isActive: activeTab === "notifications",
      onClick: handleBellClick,
    },
    {
      key: "sizes",
      label: "All Listings",
      icon: <img src="/allsizes-optimized.webp" alt="All Listings" className="w-5" />,
      isActive: activeTab === "sizes",
      onClick: handleSizesTabClick,
    },
  ];

  const listingHero = (
    <SpeciesListingHero
      title={listingTitle}
      scientificName={group.scientific_name}
      priceRange={priceRange}
      availableSizes={availableSizes.join(", ")}
      historicalSavingsPercent={bestHistoricalSavingsPercent(Object.values(group.sizes_meta || {})
        .filter((meta) => meta.has_historical_data)
        .map((meta) => meta.max_savings_percentage))}
      imageContent={headerImage}
      headerActions={headerActions}
      expanded={isExpanded}
      onClick={handleGroupVisibilityToggle}
      cardRef={listingCardRef}
    />
  );

  if (!isExpanded) return listingHero;

  return (
    <ListingGroupFrame
      cardKey={group.scientific_name}
      cardClassName="species-listing-expanded-card"
      headerContent={listingHero}
      tabs={listingTabs}
    >
          {activeTab === "careGuide" && (
            <div ref={careGuideScrollRef} className="care-guide-panel bg-white p-3 rounded" onClick={(e) => e.stopPropagation()} style={{ maxHeight: "600px", overflowY: "auto" }}>
              {careGuideLoading ? (
                <p className="text-gray-500">Loading care guide...</p>
              ) : careGuide && careGuide.sections ? (
                <CareGuideContent summary={careGuide.summary} sections={careGuide.sections} scrollContainerRef={careGuideScrollRef} />
              ) : (
                <div className="text-center py-4">
                  <p className="text-gray-500">
                    No care guide available for this species yet.
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === "priceHistory" && (
            <div className="p-3" onClick={(e) => e.stopPropagation()}>
              {isAuthenticated ? (
                <PriceHistoryGraph
                  scientificName={group.scientific_name}
                  lookbackDays={90}
                />
              ) : (
                <div>
                <p className="mb-3">Please log in to view price history.</p>
                  <Login />
                </div>
              )}
            </div>
          )}

          {activeTab === "reviews" && (
            <div className="p-3" onClick={(e) => e.stopPropagation()}>
              {loadingSpeciesReviews ? (
                <p className="text-gray-600">Loading reviews...</p>
              ) : speciesReviews.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-gray-600">
                    No reviews found for this species.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {speciesReviews.map((review) => {
                    // Get the fish review specific to this species for the collapsed view
                    const thisFishReview = review.fish_reviews.find(
                      (fishReview) =>
                        fishReview.species_listing?.species?.scientific_name?.toLowerCase() ===
                        group.scientific_name.toLowerCase()
                    );

                    return (
                      <div
                        key={review.id}
                        className="border rounded-lg p-3 bg-white"
                        onClick={() => toggleReviewExpansion(review.id)}
                      >
                        <div className="cursor-pointer">
                          <div className="flex flex-wrap justify-between items-start">
                            <div className="flex-1">
                              <div className="flex flex-row gap-2 flex-wrap items-center">
                                {/* Show fish-specific rating in collapsed view */}
                                {thisFishReview && (
                                  <div className="flex items-center">
                                    {renderStarRating(thisFishReview.rating, 1)}
                                    <span className="ml-2 font-medium text-blue-600">
                                      {thisFishReview.rating}/5
                                    </span>
                                  </div>
                                )}

                                {thisFishReview.species_listing?.size && (
                                  <span className="text-sm text-gray-600">
                                    (
                                    {sizeNameMap[
                                      thisFishReview.species_listing.size
                                    ] || thisFishReview.species_listing.size}
                                    {sizeNameMap[
                                      thisFishReview.species_listing.size
                                    ]
                                      ? ""
                                      : '"'}
                                    )
                                  </span>
                                )}

                                <p className="text-gray-800 font-semibold text-sm">
                                  from{" "}
                                  {storesData[review.store_url] ||
                                    review.store_url}
                                </p>
                                <p className="text-xs text-gray-600">
                                  by {review.user.name} on{" "}
                                  {new Date(
                                    review.created_at
                                  ).toLocaleDateString()}
                                </p>
                              </div>

                              {/* Show fish-specific notes if available, otherwise overall review text */}
                              {!expandedReviews[review.id] && (
                                <div>
                                  {thisFishReview?.notes && (
                                    <div>
                                      <p className="text-gray-700 text-sm">
                                        <span className="font-medium">
                                          Fish notes:
                                        </span>
                                      </p>
                                      <p>
                                        {thisFishReview.notes.length > 100
                                          ? thisFishReview.notes.substring(
                                              0,
                                              100
                                            ) + "..."
                                          : thisFishReview.notes}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            <span className="text-gray-400">
                              {expandedReviews[review.id] ? "▼" : "▲"}
                            </span>
                          </div>
                        </div>

                        {expandedReviews[review.id] && (
                          <div className="mt-2 pt-2 border-t">
                            {/* Show overall review information when expanded */}
                            <div className="mb-3 p-2 bg-blue-50 rounded">
                              <h4 className="font-medium text-gray-800 mb-2">
                                Overall Review
                              </h4>
                              <div className="flex items-center mb-2">
                                {renderStarRating(review.overall_rating, 1)}
                                <span className="ml-2 font-medium">
                                  {review.overall_rating}/5
                                </span>
                              </div>
                              {review.review_text && (
                                <div>
                                  <p className="text-gray-700 text-sm mb-2">
                                    <span className="font-medium">Review:</span>{" "}
                                  </p>
                                  <p>{review.review_text}</p>
                                </div>
                              )}
                              {review.order_date && (
                                <p className="text-xs text-gray-500">
                                  Order date:{" "}
                                  {new Date(
                                    review.order_date
                                  ).toLocaleDateString()}
                                </p>
                              )}
                            </div>

                            <div className="space-y-2">
                              <h4 className="font-medium text-gray-800">
                                All Fish in this Order:
                              </h4>
                              {review.fish_reviews.map((fishReview) => {
                                const isCurrentFish =
                                  fishReview.species_listing?.species?.scientific_name?.toLowerCase() ===
                                  group.scientific_name.toLowerCase();

                                return (
                                  <div
                                    key={fishReview.id}
                                    className={
                                      isCurrentFish
                                        ? "bg-blue-50 border-2 border-blue-200 p-3"
                                        : "bg-gray-50 p-3"
                                    }
                                  >
                                    <div className="flex flex-wrap justify-between items-start rounded">
                                      <div className="flex">
                                        <div className="flex items-center mb-1">
                                          <span
                                            className={`font-medium ${
                                              isCurrentFish
                                                ? "text-blue-800"
                                                : ""
                                            }`}
                                          >
                                            {
                                              fishReview.species_listing
                                                ?.species?.common_name
                                            }
                                            {isCurrentFish && " (this fish)"}
                                          </span>
                                          <span className="text-sm text-gray-600 ml-2">
                                            (
                                            {sizeNameMap[
                                              fishReview.species_listing?.size
                                            ] ||
                                              fishReview.species_listing?.size}
                                            {sizeNameMap[
                                              fishReview.species_listing?.size
                                            ]
                                              ? ""
                                              : '"'}
                                            )
                                          </span>
                                        </div>
                                      </div>
                                      <div className="flex items-center ml-4">
                                        {renderStarRating(fishReview.rating, 1)}
                                        <span className="ml-1 text-sm">
                                          {fishReview.rating}/5
                                        </span>
                                      </div>
                                    </div>
                                    {fishReview.notes && (
                                      <div>
                                        <p className="text-sm text-gray-600 mt-1">
                                          <span className="font-medium">
                                            Notes:
                                          </span>
                                        </p>
                                        <p>{fishReview.notes}</p>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleWriteReview();
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors mt-4 w-full"
              >
                Write Your Own Review
              </button>
            </div>
          )}

          {activeTab === "notifications" && (
            <div className="p-3" onClick={(e) => e.stopPropagation()}>
              {!isAuthenticated ? (
                <div className="text-center py-4">
                  <p className="text-gray-600 mb-3">
                    Please log in to set up price notifications for this fish.
                  </p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleManageNotifications(true);
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                  >
                    Go to Notifications Page
                  </button>
                </div>
              ) : loading.subscriptions ? (
                <p className="text-gray-600 text-center py-4">
                  Loading notifications...
                </p>
              ) : speciesSubscriptions.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-gray-600 mb-3">
                    No price notifications set up for this fish yet.
                  </p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleManageNotifications(true);
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                  >
                    Set Up Notifications
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {speciesSubscriptions.map((subscription) => (
                    <div
                      key={subscription.id}
                      className={`border rounded-lg p-3 ${
                        subscription.is_active
                          ? "border-green-200 bg-green-50"
                          : "border-gray-200 bg-gray-50"
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="space-y-1 text-sm">
                            <p className="text-gray-600">
                              <strong>Sizes:</strong>{" "}
                              {subscription.sizes
                                .map((size) => formatSizeDisplay(size))
                                .join(", ")}
                            </p>

                            {subscription.max_price && (
                              <p className="text-gray-600">
                                <strong>Max Price:</strong> $
                                {subscription.max_price}
                              </p>
                            )}

                            <p className="text-gray-600">
                              <strong>Created:</strong>{" "}
                              {new Date(
                                subscription.created_at
                              ).toLocaleDateString()}
                            </p>

                            {subscription.last_sent && (
                              <p className="text-gray-600">
                                <strong>Last Notification:</strong>{" "}
                                {new Date(
                                  subscription.last_sent
                                ).toLocaleDateString()}
                              </p>
                            )}
                          </div>

                          <div
                            className={`mt-2 inline-block px-2 py-1 rounded text-xs font-medium ${
                              subscription.is_active
                                ? "bg-green-200 text-green-800"
                                : "bg-gray-200 text-gray-800"
                            }`}
                          >
                            {subscription.is_active ? "Active" : "Inactive"}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleManageNotifications(false);
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors w-full"
                  >
                    Manage All Notifications
                  </button>
                </div>
              )}
            </div>
          )}

        {activeTab === "sizes" && (
          <div className="listing-details-panel p-3" onClick={(e) => e.stopPropagation()}>
            {/* Graph and Filters Row - side by side on wide screens */}
            <div className="listing-details-controls flex flex-wrap gap-3 mb-3">
              {/* Best Price Summary Chart */}
              <div className="flex-1 grow">
                <BestPriceSummary
                  bestPricesPerSize={bestPricesPerSize}
                  sortSizes={sortSizes}
                  storesData={storesData}
                />
              </div>

              {/* Size Filters */}
              <SizeFilter
                includeNumericSizes={includeNumericSizesLocal}
                setIncludeNumericSizes={setIncludeNumericSizesLocal}
                sizeRange={numericSizeRange}
                sizeFilter={listingSizeFilter}
                onSizeFilterChange={setListingSizeFilter}
                includeNonNumericSizes={includeNonNumericSizesLocal}
                setIncludeNonNumericSizes={setIncludeNonNumericSizesLocal}
                availableNonNumericSizes={availableNonNumericSizes}
                selectedNonNumericSizes={selectedNonNumericSizes}
                onNonNumericSizeChange={(size) => {
                  setSelectedNonNumericSizes((prev) =>
                    prev.includes(size)
                      ? prev.filter((s) => s !== size)
                      : [...prev, size]
                  );
                }}
                compact={true}
              />
            </div>

            {/* Mobile Sort Dropdown - Only visible on small screens */}
            <div className="listing-mobile-sort block visible-on-mobile mb-3">
              <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sort by
                </label>
                <div className="listing-mobile-sort-fields flex flex-col justify-between gap-3">
                  <select
                    value={listingSortKey}
                    onChange={(e) => setListingSortKey(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded text-sm"
                  >
                    <option value="bestSavingsPercentage">
                      Best Deal (% Savings)
                    </option>
                    <option value="pricePerFish">Price Per Each</option>
                    <option value="price">Total Price</option>
                    <option value="sizeMin">Size</option>
                    <option value="source">Store</option>
                  </select>
                  <select
                    value={listingSortDir}
                    onChange={(e) => setListingSortDir(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded text-sm"
                  >
                    <option value="asc">Ascending</option>
                    <option value="desc">Descending</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Listing Count */}
            <div className="listing-count text-sm text-gray-600 mb-3">
              Showing {filteredSortedListings.length} of{" "}
              {deduplicatedListings.length} listings
            </div>

            {/* Listings Table */}
            <ListingDetailsTable
              desktopInitialRowCount={5}
              mobileInitialRowCount={3}
              columns={[
                {
                  key: "title",
                  label: "Listing",
                  width: "w-[35%]",
                  sortable: true,
                  defaultSortDirection: "desc",
                  sortValue: (listing) => listing.title || "",
                },
                {
                  key: "sizeMin",
                  label: "Size",
                  width: "w-[15%]",
                  align: "text-center",
                  sortable: true,
                  defaultSortDirection: "desc",
                  sortValue: (listing) => listing.sizeMin,
                },
                {
                  key: "pricePerFish",
                  label: "Price Per Each",
                  mobileLabel: "Each",
                  width: "w-[15%]",
                  align: "text-center",
                  sortable: true,
                  defaultSortDirection: "asc",
                  sortValue: (listing) => listing.pricePerFish,
                },
                {
                  key: "bestSavingsPercentage",
                  label: "Savings",
                  width: "w-[15%]",
                  align: "text-center",
                  sortable: true,
                  defaultSortDirection: "desc",
                  sortValue: (listing) => listing.bestSavingsPercentage,
                },
                {
                  key: "source",
                  label: "Store",
                  width: "w-[20%]",
                  align: "text-right",
                  sortable: true,
                  defaultSortDirection: "desc",
                  sortValue: (listing) =>
                    storesData[listing.source] || listing.source,
                },
              ]}
              rows={filteredSortedListings}
              sortColumns={[
                {
                  key: "price",
                  sortable: true,
                  sortValue: (listing) => listing.price,
                },
              ]}
              sortConfig={{ key: listingSortKey, direction: listingSortDir }}
              onSortChange={(nextSortConfig) => {
                setListingSortKey(nextSortConfig.key);
                setListingSortDir(nextSortConfig.direction);
              }}
              renderRow={(listing, idx) => {
                const savingsColor = getDealTextColor(
                  listing.bestSavingsPercentage
                );
                const hasSavings = listing.bestSavingsPercentage > 0;

                return (
                  <tr
                    key={`${listing.product_url}-${idx}`}
                    className={`border-b border-gray-200 cursor-pointer hover:bg-indigo-50 active:bg-indigo-100 transition-colors ${
                      hasSavings
                        ? "bg-green-50"
                        : idx % 2 === 0
                        ? "bg-gray-50"
                        : "bg-white"
                    }`}
                    onClick={() => {
                      window.open(
                        getFishProductUrl(
                          group.scientific_name,
                          listing.sizes[0],
                          listing.product_url
                        ),
                        "_blank",
                        "noopener,noreferrer"
                      );
                      ReactGA.event({
                        category: "Interaction",
                        action: "Click Fish Link",
                        label: `${group.scientific_name} - ${listing.title}`,
                        value: listing.price,
                      });
                      trackFishInteraction("click_fish_link", {
                        scientific_name: group.scientific_name,
                        common_name: group.common_name,
                        size: formatSizeRange(listing),
                        store: storesData[listing.source],
                        price: listing.price,
                        is_lowest_price: idx === 0,
                        fish_title: listing.title,
                        savings_percentage: listing.bestSavingsPercentage,
                      });
                    }}
                    data-posthog="listing-fish-link"
                    data-posthog-scientific-name={group.scientific_name}
                    data-posthog-size={formatSizeRange(listing)}
                    data-posthog-product-url={listing.product_url}
                    data-posthog-store={storesData[listing.source]}
                  >
                    <td className="py-1.5 px-2" data-label="Listing">
                      <div
                        className="font-medium text-gray-900 text-wrap text-sm"
                        title={listing.title}
                      >
                        {listing.title}
                      </div>
                      {listing.number_of_individuals > 1 && (
                        <div className="text-xs text-gray-500">
                          Qty: {listing.number_of_individuals} ($
                          {listing.price.toFixed(2)} total)
                        </div>
                      )}
                    </td>
                    <td className="py-1.5 px-2 text-center" data-label="Size">
                      <span className="text-gray-700 text-sm">
                        {formatSizeRange(listing)}
                      </span>
                    </td>
                    <td
                      className="py-1.5 px-2 text-center"
                      data-label="Price Per Each"
                    >
                      <span
                        className={`font-bold text-sm ${
                          hasSavings ? "text-green-600" : ""
                        }`}
                      >
                        ${listing.pricePerFish.toFixed(2)}
                      </span>
                    </td>
                    <td
                      className="py-1.5 px-2 text-center"
                      data-label="Savings"
                    >
                      <div className="flex flex-col text-xs" title="Savings for this size against today's average offers and the typical price">
                        <span className={listing.currentSavingsPercentage == null ? "text-gray-400" : getDealTextColor(listing.currentSavingsPercentage)}>
                          Today: {listing.currentSavingsPercentage == null ? "—" : `${listing.currentSavingsPercentage.toFixed(0)}%`}
                        </span>
                        <span className={savingsColor}>Typical: {listing.historicalSavingsPercentage == null ? "—" : `${listing.historicalSavingsPercentage.toFixed(0)}%`}</span>
                      </div>
                    </td>
                    <td className="py-1.5 px-2 text-right" data-label="Store">
                      <div className="flex items-center justify-end gap-1">
                        <StoreName
                          name={storesData[listing.source] || listing.source}
                          className="text-gray-700 text-sm"
                        />
                        <img
                          src="/external-link-optimized.webp"
                          alt="External Link"
                          className="inline-block h-3 w-3 flex-shrink-0"
                        />
                      </div>
                    </td>
                  </tr>
                );
              }}
              emptyContent={
                <div className="p-5 text-center text-gray-500">
                  No listings match your filter criteria. Try adjusting the size
                  range or enabling more size categories.
                </div>
              }
            />
          </div>
        )}
    </ListingGroupFrame>
  );
};


export default FishListing;
